import { useState, useMemo, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Users, Info } from "lucide-react";
import { api, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

// Configurateur traiteur : portions familiales (invités -> recommandation) + groupes d'options réutilisables.
export default function ConfiguratorSheet({ product, open, onClose }) {
  const { addItem } = useCart();
  const isFamily = !!product.configurable;
  const perPortion = product.servings || (isFamily ? 4 : 1);
  const [guests, setGuests] = useState(perPortion);
  const [portions, setPortions] = useState(1);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (open && product.option_group_ids && product.option_group_ids.length) {
      api.get("/option-groups").then((r) =>
        setGroups(r.data.filter((g) => product.option_group_ids.includes(g.id)))
      );
    } else {
      setGroups([]);
    }
  }, [open, product]);

  const applyRecommended = () => setPortions(Math.max(1, Math.ceil(guests / perPortion)));

  const toggleOption = (g, idx) => {
    setSelected((prev) => {
      const cur = prev[g.id] || [];
      if (g.type === "single") return { ...prev, [g.id]: [idx] };
      let next = cur.includes(idx) ? cur.filter((i) => i !== idx) : [...cur, idx];
      if (g.max_select && next.length > g.max_select) next = next.slice(next.length - g.max_select);
      return { ...prev, [g.id]: next };
    });
  };

  const supplements = useMemo(() => {
    let sum = 0;
    groups.forEach((g) => (selected[g.id] || []).forEach((i) => { sum += g.options[i]?.prix_supplement || 0; }));
    return sum;
  }, [groups, selected]);

  const units = isFamily ? portions : qty;
  const unitPrice = product.prix + supplements;
  const total = useMemo(() => unitPrice * units, [unitPrice, units]);

  const selectedList = () => {
    const out = [];
    groups.forEach((g) => (selected[g.id] || []).forEach((i) => {
      const o = g.options[i];
      if (o) out.push({ group: g.nom, nom: o.nom, prix_supplement: o.prix_supplement || 0 });
    }));
    return out;
  };

  const confirm = () => {
    const missing = groups.find((g) => (g.min_select || 0) > (selected[g.id] || []).length);
    if (missing) { toast.error(`Veuillez choisir ${missing.min_select} option(s) pour « ${missing.nom} »`); return; }
    addItem({
      product_id: product.id, nom: product.nom, prix: unitPrice, image: product.image,
      servings: perPortion, configurable: true, quantity: units,
      guests: isFamily ? guests : null, note, options: selectedList(),
    });
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        data-testid="configurator-sheet"
        className="w-full sm:max-w-md bg-card border-l border-border text-foreground p-0 flex flex-col"
      >
        <SheetHeader className="p-0">
          <div className="dark relative h-44">
            <img src={product.image} alt={product.nom} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
            <SheetTitle className="absolute bottom-4 left-5 right-5 text-left font-heading text-xl font-bold text-foreground">
              {product.nom}
            </SheetTitle>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {product.description && <p className="text-sm text-muted-foreground">{product.description}</p>}

          {isFamily && (
            <>
              <div className="flex items-start gap-2 p-3 rounded-xl bg-muted border border-border text-sm">
                <Info className="h-4 w-4 text-coral shrink-0 mt-0.5" />
                <span className="text-foreground">Format familial — chaque portion sert environ <strong>{perPortion} personnes</strong>.</span>
              </div>
              <div>
                <label className="font-heading text-sm font-semibold text-foreground">Nombre d'invités</label>
                <div className="flex items-center gap-4 mt-3">
                  <Stepper value={guests} setValue={(v) => setGuests(Math.max(1, v))} testid="config-guests" />
                  <div className="flex items-center gap-1.5 text-muted-foreground text-sm"><Users className="h-4 w-4 text-coral" /> invités</div>
                </div>
                <button onClick={applyRecommended} data-testid="config-apply-reco" className="mt-3 text-sm text-coral hover:underline">
                  Recommandé : {Math.max(1, Math.ceil(guests / perPortion))} portion(s) →
                </button>
              </div>
              <div>
                <label className="font-heading text-sm font-semibold text-foreground">Nombre de portions</label>
                <p className="text-xs text-muted-foreground mb-3">Vous pouvez ajuster la recommandation.</p>
                <Stepper value={portions} setValue={(v) => setPortions(Math.max(1, v))} testid="config-portions" />
                <p className="text-xs text-muted-foreground mt-2">Couvre environ {portions * perPortion} personnes</p>
              </div>
            </>
          )}

          {groups.map((g) => (
            <div key={g.id} data-testid={`config-group-${g.id}`}>
              <label className="font-heading text-sm font-semibold text-foreground">{g.nom}</label>
              <p className="text-xs text-muted-foreground mb-3">
                {g.type === "single" ? "Choisissez 1 option" : `Choisissez ${g.min_select || 0}${g.max_select ? ` à ${g.max_select}` : "+"} option(s)`}
              </p>
              <div className="space-y-2">
                {g.options.map((o, idx) => {
                  const on = (selected[g.id] || []).includes(idx);
                  return (
                    <button key={idx} type="button" onClick={() => toggleOption(g, idx)}
                      data-testid={`config-option-${g.id}-${idx}`}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "bg-foreground/5 border-border hover:border-foreground/40"}`}>
                      <span>{o.nom}</span>
                      <span>{o.prix_supplement ? `+ ${money(o.prix_supplement)}` : ""}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {!isFamily && (
            <div>
              <label className="font-heading text-sm font-semibold text-foreground">Quantité</label>
              <div className="mt-3"><Stepper value={qty} setValue={(v) => setQty(Math.max(1, v))} testid="config-qty" /></div>
            </div>
          )}

          <div>
            <label className="font-heading text-sm font-semibold text-foreground">Note (optionnel)</label>
            <textarea
              data-testid="config-note"
              value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} rows={2}
              placeholder="Allergies, préférences..."
              className="mt-2 w-full px-4 py-3 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none resize-none text-sm"
            />
          </div>
        </div>

        <div className="p-5 border-t border-border glass">
          <div className="flex items-center justify-between mb-3">
            <span className="text-muted-foreground text-sm">Total ({units} × {money(unitPrice)})</span>
            <span className="font-heading text-2xl font-bold text-coral" data-testid="config-total">{money(total)}</span>
          </div>
          <button
            onClick={confirm}
            data-testid="config-add-to-cart"
            className="w-full py-3.5 rounded-xl bg-primary hover:opacity-90 text-primary-foreground font-heading font-semibold transition-colors active:scale-[0.98]"
          >
            Ajouter au panier
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Stepper({ value, setValue, testid }) {
  return (
    <div className="inline-flex items-center rounded-full bg-foreground/5 border border-border">
      <button data-testid={`${testid}-minus`} onClick={() => setValue(value - 1)} className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-foreground/10 transition-colors">
        <Minus className="h-4 w-4" />
      </button>
      <span data-testid={`${testid}-value`} className="w-12 text-center font-heading font-bold text-lg">{value}</span>
      <button data-testid={`${testid}-plus`} onClick={() => setValue(value + 1)} className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-foreground/10 transition-colors">
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
